/**
 * The desk's «ثبت نوبت» on the slot ENGINE (PM-0468 §2.4 / §4.3,
 * docs/SERVICE_OFFERING_AND_BOOKING_DESIGN.md). Pure helpers — no React, no
 * network — so the dialog's decisions can be proved in isolation:
 *
 *   • which service rows form ONE pickable entry («ویزیت» is many per-doctor
 *     declarations on one catalogue row);
 *   • who performs it, and which row prices THAT performer;
 *   • how long it takes, and where that number came from (PM-0160 §7);
 *   • the first free instant and the next few after a refused one;
 *   • the engine's `reason` on a cell, as the kit `SlotGrid` wants it;
 *   • which FIELD a server refusal belongs to.
 *
 * Nothing here guesses a time: instants come from `bookableSlots` (anchored at
 * the shift start on the clinic's clock) and are never built with `setHours`.
 */

/**
 * The people an entry's service may be given to — its performers.
 *
 * The declaration names them as a doctor, a staff member or a group, and the
 * BFF has already expanded a group to its holders (`members`). Inactive people
 * are dropped, duplicates across groups merged, and the declaration's default
 * comes first. `role` is the group's name when the person came in through one
 * («فیزیوتراپیست»), which says more than «همکار».
 */
export function servicePerformers(source) {
  const seen = new Map();
  for (const perf of source?.performers || []) {
    for (const m of perf.members || []) {
      if (m.active === false) continue;
      const key = `${m.type}:${m.id}`;
      const prev = seen.get(key);
      const isDefault = !!(perf.isDefault && perf.type !== 'ROLE');
      if (!prev) {
        seen.set(key, {
          key, type: m.type, id: m.id, name: m.name || m.id, isDefault,
          role: perf.type === 'ROLE' ? perf.label : '',
        });
      } else {
        prev.isDefault = prev.isDefault || isDefault;
        if (!prev.role && perf.type === 'ROLE') prev.role = perf.label;
      }
    }
  }
  return [...seen.values()].sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
}

/** «پزشک» / «فیزیوتراپیست» / «همکار» — what a performer is, in one word. */
export const performerKind = (p, t) =>
  p.role || (p.type === 'DOCTOR' ? t('common.entity.doctor') : t('reception.paraclinic.coworker'));

/**
 * One pickable ENTRY per catalogue id. A consultation exists once per
 * practitioner (each their own price row on `consult-visit`); the desk picks
 * «ویزیت» once and the performer decides which row prices it. Performers are
 * the union over the rows; the default is the first row's starred one.
 */
export function groupBookableServices(rows) {
  const byCatalog = new Map();
  for (const row of rows || []) {
    if (!row?.catalogId || row.isActive === false) continue;
    const key = String(row.catalogId);
    let entry = byCatalog.get(key);
    if (!entry) {
      entry = {
        catalogId: key, name: row.name, orderType: row.orderType || null,
        schedulingMode: row.schedulingMode || 'TIMED', rows: [], performers: [],
      };
      byCatalog.set(key, entry);
    }
    entry.rows.push(row);
    // A doctor's own price row names only them; the union keeps everyone once.
    const people = servicePerformers(row);
    for (const p of people) {
      if (!entry.performers.some((q) => q.key === p.key)) entry.performers.push(p);
      else if (p.isDefault) entry.performers.find((q) => q.key === p.key).isDefault = true;
    }
  }
  const entries = [...byCatalog.values()].map((e) => ({
    ...e,
    performers: [...e.performers].sort((a, b) => Number(b.isDefault) - Number(a.isDefault)),
  }));
  return {
    visits: entries.filter((e) => e.orderType === 'consultation'),
    services: entries.filter((e) => e.orderType !== 'consultation'),
  };
}

/** An entry built from an ORDER row («اقدام‌های درمانی» › «نوبت بده»): the service is a fact. */
export function entryFromOrder(order) {
  if (!order) return null;
  return {
    catalogId: String(order.catalogId || order.declarationId || ''),
    name: order.description || '',
    orderType: order.orderType || null,
    schedulingMode: order.schedulingMode || (order.requiresAppointment === false ? 'QUEUE' : 'TIMED'),
    rows: [{
      declarationId: order.declarationId, catalogId: order.catalogId, doctorId: null,
      // A session's length is the DECLARATION's (bookServiceSession reads it there);
      // an order row that predates the field carries none, and the engine still
      // needs a number to draw the grid — 30 is the declaration default it was
      // written with, not a guess about the service.
      durationMinutes: order.durationMinutes || 30, durationSource: order.durationMinutes ? 'SERVICE' : 'UNSET',
      unitPrice: order.unitPrice, currency: order.currency,
    }],
    performers: servicePerformers(order),
    order,
  };
}

/** The row that prices THIS performer: a doctor's own row, else the clinic-wide one. */
export function performerRow(entry, performer) {
  const rows = entry?.rows || [];
  if (!rows.length) return null;
  if (performer?.type === 'DOCTOR') {
    return rows.find((r) => r.doctorId === performer.id) || rows.find((r) => !r.doctorId) || rows[0];
  }
  return rows.find((r) => !r.doctorId) || rows[0];
}

/**
 * The slot length and its SOURCE (PM-0160 §7): the service's own number first;
 * for a visit with none, the practitioner's «مدت هر ویزیت» (null on the row =
 * the membership's default of 30, which «همکاران» shows as «پیش‌فرض ۳۰»);
 * otherwise nothing — the engine never guesses, so the caller must say so.
 */
export function resolveDuration(entry, performer, doctorRow) {
  const row = performerRow(entry, performer);
  if (row && Number(row.durationMinutes) > 0) return { minutes: Number(row.durationMinutes), source: 'SERVICE' };
  if (performer?.type === 'DOCTOR' && doctorRow) {
    return { minutes: Number(doctorRow.visitDurationMinutes) > 0 ? Number(doctorRow.visitDurationMinutes) : 30, source: 'DOCTOR' };
  }
  return { minutes: null, source: null };
}

/** The ★ performer, else the one a caller preset, else the first. */
export function defaultPerformer(people, presetId = null) {
  if (!people?.length) return null;
  if (presetId) {
    const hit = people.find((p) => p.id === presetId);
    if (hit) return hit;
  }
  return people.find((p) => p.isDefault) || people[0];
}

/** The entry to open on: the preset, else «ویزیت» (the 90 %), else the first service. */
export function defaultEntry(groups, presetCatalogId = null) {
  const all = [...(groups?.visits || []), ...(groups?.services || [])];
  if (presetCatalogId) {
    const hit = all.find((e) => e.catalogId === String(presetCatalogId));
    if (hit) return hit;
  }
  return groups?.visits?.[0] || groups?.services?.[0] || null;
}

/** The first free instant after `nowMs` across the engine's days, in order. */
export function firstFree(days, nowMs = Date.now()) {
  for (const day of days || []) {
    for (const s of day.slots || []) {
      if (s.free && Number(s.startMs) > nowMs) return { startMs: Number(s.startMs), date: day.date };
    }
  }
  return null;
}

/** The next `n` free instants strictly after `afterMs` — the chips a refused hour offers. */
export function nextFree(days, afterMs, n = 3) {
  const out = [];
  for (const day of days || []) {
    for (const s of day.slots || []) {
      if (s.free && Number(s.startMs) > afterMs) {
        out.push({ startMs: Number(s.startMs), date: day.date });
        if (out.length >= n) return out;
      }
    }
  }
  return out;
}

/** One day's slots out of the engine's answer; [] when the day is not in it or has no shift. */
export function slotsOn(days, iso) {
  return (days || []).find((d) => d.date === iso)?.slots || [];
}

/**
 * The engine's cells as the kit `SlotGrid` wants them. MARK (decision Q3): an
 * OUTSIDE_SERVICE_WINDOW hour stays CHOOSABLE and is marked — the kit's `held`
 * is its amber affordance (the kit has no `tone` prop yet; CL §5 gap) — and the
 * dialog asks before booking it. Every other non-free reason disables the cell.
 */
export function gridSlots(slots, { formatTime, t, nowMs = Date.now() } = {}) {
  return (slots || []).map((s) => {
    const past = Number(s.startMs) <= nowMs;
    const reason = past && s.free ? 'PAST' : (s.free ? null : s.reason || 'BUSY');
    const mark = reason === 'OUTSIDE_SERVICE_WINDOW';
    return {
      value: String(s.startMs),
      label: formatTime ? formatTime(Number(s.startMs)) : String(s.startMs),
      disabled: !!reason && !mark,
      held: mark,
      reason,
      title: reason && t ? t(`reception.booking.reason.${reason}`) : undefined,
    };
  });
}

/**
 * Which FIELD a refusal belongs to (DESIGN_CONCEPT §5.3 / §4.3): the engine's
 * codes land on the cell or the chip they are about, never in a generic banner.
 */
export function mapBookingError(e, t) {
  const code = e?.extensions?.code || '';
  const message = e?.message || t('errors.reception.bookFailed');
  if (code === 'SERVICE_NOT_IN_SHIFT') return { field: 'time', code, message };
  if (code === 'PROVIDER_NOT_PRESENT' || code === 'DOCTOR_NOT_PRESENT') return { field: 'performer', code, message };
  if (code === 'APPOINTMENT_CONFLICT') return { field: 'time', code, message: t('reception.booking.doctorBusy'), nextFree: true };
  if (code === 'NO_SLOT_AVAILABLE') return { field: 'time', code, message: t('reception.booking.noSlotToday') };
  if (code === 'PERFORMER_NOT_ALLOWED' || code === 'PERFORMER_IS_A_GROUP') return { field: 'performer', code, message };
  return { field: 'form', code, message };
}

/** A client request id (B07): the same id on a retry, a new one after success. */
export function newRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
